import { ReactElement, useCallback, useRef } from "react";

import { SxProps, Typography } from "@mui/material";

import * as Events from "@app/analytics/tracking/events";
import { useAuthenticated } from "@app/common/auth/useAuth";
import useMeasure from "@app/common/hooks/useMeasure";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import * as Workspace from "@app/common/workspaces/workspaceUtils";

import EditTypography from "@rui/EditTypography";
import { Row } from "@rui/Stack";

interface CommonProps {
  title: string;
  subtitle?: string;
  actions?: ReactElement[] | ReactElement;
  sx?: SxProps;
}

interface WorkspaceWorkspaceHeaderProps {
  id?: undefined;
  type: "workspace";
  canEdit?: boolean;
}

interface WorkspaceTeamHeaderProps {
  id: string;
  type: "team";
  canEdit: boolean;
}

type WorkspaceHeaderProps = CommonProps &
  (WorkspaceTeamHeaderProps | WorkspaceWorkspaceHeaderProps);

const getTotalWidth = (el: HTMLElement | null) => {
  if (!el) {
    return 0;
  }
  const { marginLeft, marginRight } = window.getComputedStyle(el);
  return el.offsetWidth + parseFloat(marginLeft) + parseFloat(marginRight);
};

const WorkspaceHeader = ({
  id,
  title,
  subtitle,
  type,
  actions,
  canEdit = false,
  sx,
}: WorkspaceHeaderProps) => {
  const { user } = useAuthenticated();
  const isWorkspaceAdmin =
    type === "workspace" && Workspace.isWorkspaceAdmin(user.workspace);

  const { updateWorkspaceName, updateTeamName } = useWorkspace();

  const [containerRef, _contentRect, { innerWidth }] = useMeasure();
  const rContentRef = useRef<HTMLDivElement | null>(null);
  const tContentRef = useRef<HTMLElement | null>(null);

  const titleMaxWidth =
    innerWidth -
    getTotalWidth(rContentRef.current) -
    getTotalWidth(tContentRef.current) -
    12;

  const handleNameChange = useCallback(
    (name: string) => {
      if (name === title || !(isWorkspaceAdmin || canEdit)) {
        return;
      }
      Events.editName({ entity: type });
      if (type === "workspace") {
        updateWorkspaceName({ name });
      } else if (type === "team") {
        updateTeamName({ teamId: id, name });
      }
    },
    [title]
  );

  return (
    <Row
      variant="apart"
      sx={{ pb: 2, height: "52px", ...sx }}
      ref={containerRef}
    >
      <Row variant="left" spacing={2}>
        {canEdit || isWorkspaceAdmin ? (
          <EditTypography
            variant="bodyPrimaryMed"
            value={title}
            onEditChange={handleNameChange}
            maxWidth={titleMaxWidth}
            minWidth={0}
            floatingButtons
          />
        ) : (
          <Typography variant="bodyPrimaryMed">{title}</Typography>
        )}
        <Typography variant="bodySecondaryReg" ref={tContentRef}>
          {subtitle}
        </Typography>
      </Row>
      <Row spacing={2} ref={rContentRef}>
        {actions}
      </Row>
    </Row>
  );
};

export default WorkspaceHeader;
